import { WorkShiftConfiguration } from '../interfaces/interfaces';
import { extractHHmm, extractMinutesFromApiTime, isZeroDurationRange } from './time-format.util';
import { dateKey, isColombianHoliday } from './work-schedule-calendar.util';
import { colorForTurnoIndex } from './work-shift-color.util';
import { ScheduleSource, WorkShiftRotation } from './work-shift-rotation.util';

/**
 * Lógica pura para "Programación masiva de horarios" (vista por proyecto).
 * dayNumber sigue la misma convención que WorkShiftSchedule.Day: 0=Domingo, 1=Lunes, ... 6=Sábado.
 */

export type ShiftLetter = 'M' | 'T' | 'N' | 'E' | 'S' | 'D';

export const LETTER_LABELS: Record<ShiftLetter, string> = {
  E: 'Jornada estándar',
  M: 'Turno mañana',
  T: 'Turno tarde',
  N: 'Turno nocturno',
  S: 'Sábado medio día',
  D: 'Descanso'
};

/** Paleta fija por tipo de jornada (no por patrón): todo turno con la misma letra base
 * usa siempre el mismo color, sin importar a qué patrón pertenezca. */
export const LETTER_COLORS: Record<ShiftLetter, string> = {
  E: '#2E7DD1', // azul - Jornada estándar
  M: '#3FA66B', // verde - Turno mañana
  T: '#E8912D', // naranja - Turno tarde
  N: '#8B5CF6', // púrpura - Turno nocturno
  S: '#17A2A8', // cian - Sábado medio día
  D: '#94A3B8'  // gris - Descanso
};

export function lightenColor(hex: string, amount: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return `#${[mix(r), mix(g), mix(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

export interface ShiftPatternDay {
  dayNumber: number;
  hasSchedule: boolean;
  startHour: string | null;
  endHour: string | null;
  workedHours: number;
  letter: ShiftLetter;
  /** Color del turno real que corresponde a este día (mismo horario exacto), si se encontró
   * uno en el catálogo del proyecto. undefined = no hay match, usar el color del patrón. */
  color?: string;
}

export interface ShiftPattern {
  id: string;
  name: string;
  isActive: boolean;
  color: string;
  days: ShiftPatternDay[]; // longitud 7, indexado por dayNumber (0..6)
  totalWeekHours: number;
  /** true = turno diario (WorkShift.Daily.IsDaily), false = patrón semanal. */
  isDaily: boolean;
}

function getBaseLetterFromName(name: string): ShiftLetter {
  const value = (name || '').toLowerCase();
  if (value.includes('noc')) { return 'N'; }
  if (value.includes('tard')) { return 'T'; }
  if (value.includes('mañ') || value.includes('man')) { return 'M'; }
  return 'E';
}

/** Paleta por posición (no por categoría): cada turno/patrón del catálogo recibe un color
 * distinto de sus vecinos, sin importar cuántos compartan el mismo tipo de jornada. */
const DISTINCT_COLOR_PALETTE = [
  '#1F9D7C', '#2F3E9E', '#D6428F', '#C98A1D', '#3F7FE0', '#8B5CF6', '#0EA5A5',
  '#DB5A42', '#4C9F45', '#B23A6B', '#5C6BC0', '#E0912D', '#159947', '#7C4DFF'
];

function colorForIndex(index: number): string {
  return DISTINCT_COLOR_PALETTE[index % DISTINCT_COLOR_PALETTE.length];
}

/** Normaliza con extractHHmm antes de comparar — el turno diario y el patrón semanal pueden
 * traer la misma hora en formatos "sueltos" distintos de la API (con/sin segundos), así que
 * comparar las cadenas crudas puede fallar y dejar el día del patrón sin el color de su turno. */
function scheduleHoursKey(startHour: string | null, endHour: string | null): string | null {
  return startHour && endHour ? `${extractHHmm(startHour)}|${extractHHmm(endHour)}` : null;
}

function computeWorkedHoursForSchedule(schedule: { Day: number; EndDay?: number; StartHour: string; EndHour: string; BreakTime?: string }): number {
  const startMinutes = extractMinutesFromApiTime(schedule.StartHour);
  const endMinutes = extractMinutesFromApiTime(schedule.EndHour);
  const endDay = schedule.EndDay ?? schedule.Day;
  const dayDiff = endDay >= schedule.Day ? endDay - schedule.Day : (7 - schedule.Day) + endDay;
  const breakMinutes = extractMinutesFromApiTime(schedule.BreakTime);
  const totalMinutes = (endMinutes + dayDiff * 24 * 60) - startMinutes - breakMinutes;
  return Math.max(0, totalMinutes) / 60;
}

function getModalDuration(durations: number[]): number {
  if (durations.length === 0) { return 0; }
  const buckets = new Map<number, number>();
  durations.forEach(duration => {
    const bucketKey = Math.round(duration * 4) / 4; // agrupa en cuartos de hora
    buckets.set(bucketKey, (buckets.get(bucketKey) || 0) + 1);
  });
  let modalKey = durations[0];
  let modalCount = 0;
  buckets.forEach((count, key) => {
    if (count > modalCount) {
      modalCount = count;
      modalKey = key;
    }
  });
  return modalKey;
}

function buildShiftPatternFromWorkShift(
  workShift: WorkShiftConfiguration,
  color: string,
  turnoByHours?: Map<string, string>
): ShiftPattern {
  const baseLetter = getBaseLetterFromName(workShift.Name);
  const schedulesByDay = new Map<number, typeof workShift.Schedule[number]>();
  (workShift.Schedule || []).forEach(schedule => schedulesByDay.set(schedule.Day, schedule));

  const workedDurations: number[] = [];
  Array.from(schedulesByDay.values()).forEach(schedule => {
    if (isZeroDurationRange(schedule.StartHour, schedule.EndHour)) { return; }
    workedDurations.push(computeWorkedHoursForSchedule(schedule));
  });
  const modalDuration = getModalDuration(workedDurations);

  const days: ShiftPatternDay[] = [];
  for (let dayNumber = 0; dayNumber < 7; dayNumber++) {
    const schedule = schedulesByDay.get(dayNumber);
    if (!schedule || isZeroDurationRange(schedule.StartHour, schedule.EndHour)) {
      days.push({ dayNumber, hasSchedule: false, startHour: null, endHour: null, workedHours: 0, letter: 'D' });
      continue;
    }
    const workedHours = computeWorkedHoursForSchedule(schedule);
    const letter: ShiftLetter = workedHours < modalDuration - 0.01 ? 'S' : baseLetter;
    const matchedColor = turnoByHours?.get(scheduleHoursKey(schedule.StartHour, schedule.EndHour) || '');
    days.push({
      dayNumber,
      hasSchedule: true,
      startHour: schedule.StartHour,
      endHour: schedule.EndHour,
      workedHours,
      letter,
      color: matchedColor
    });
  }

  return {
    id: workShift.Id,
    name: workShift.Name,
    isActive: workShift.IsActive,
    color,
    days,
    totalWeekHours: days.reduce((sum, day) => sum + day.workedHours, 0),
    isDaily: !!workShift.Daily?.IsDaily
  };
}

/** Punto de entrada para pintar el sidebar de "Turnos"/"Patrones": a diferencia del catálogo
 * embebido en Schedule/Bulk, este viene del catálogo completo del proyecto (GET
 * api/WorkShift/Project/{id}), así que puede darle a cada turno un color distinto por posición
 * y, con eso, colorear cada día de un patrón semanal con el color del turno real que coincide
 * exactamente en horario — en vez de un color genérico por "tipo de jornada".
 */
export function buildShiftPatternsFromWorkShifts(list: WorkShiftConfiguration[]): ShiftPattern[] {
  const dailyList = (list || []).filter(ws => !!ws.Daily?.IsDaily);
  const patternList = (list || []).filter(ws => !ws.Daily?.IsDaily);

  // Mismo índice y misma paleta que "Configuración de horario de trabajo por proyecto" (que
  // lista estos mismos turnos diarios con el mismo filtro/orden) — así un turno se ve con el
  // mismo color en las dos pantallas.
  const dailyPatterns = dailyList.map((ws, index) => buildShiftPatternFromWorkShift(ws, colorForTurnoIndex(index)));

  const turnoByHours = new Map<string, string>();
  dailyPatterns.forEach(pattern => {
    const scheduledDay = pattern.days.find(day => day.hasSchedule);
    if (!scheduledDay) { return; }
    const key = scheduleHoursKey(scheduledDay.startHour, scheduledDay.endHour);
    if (key) { turnoByHours.set(key, pattern.color); }
  });

  const weeklyPatterns = patternList.map((ws, index) =>
    buildShiftPatternFromWorkShift(ws, colorForIndex(dailyList.length + index), turnoByHours));

  return [...dailyPatterns, ...weeklyPatterns];
}

export interface WeekDay {
  date: Date;
  dayNumber: number; // 0=Domingo..6=Sabado
  shortLabel: string;
  dayOfMonth: number;
  isWeekend: boolean;
  isHoliday: boolean;
}

const SHORT_DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTH_ABBREV = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** weekStartDate debe ser el lunes de la semana a mostrar. Devuelve Lunes..Domingo. */
export function buildWeekDays(weekStartDate: Date): WeekDay[] {
  const days: WeekDay[] = [];
  for (let offset = 0; offset < 7; offset++) {
    const date = new Date(weekStartDate);
    date.setDate(weekStartDate.getDate() + offset);
    const dayNumber = date.getDay();
    days.push({
      date,
      dayNumber,
      shortLabel: SHORT_DAY_LABELS[dayNumber],
      dayOfMonth: date.getDate(),
      isWeekend: dayNumber === 0 || dayNumber === 6,
      isHoliday: isColombianHoliday(date)
    });
  }
  return days;
}

export function formatWeekRangeLabel(weekStartDate: Date): string {
  const weekEndDate = new Date(weekStartDate);
  weekEndDate.setDate(weekStartDate.getDate() + 6);

  const startDay = weekStartDate.getDate();
  const endDay = weekEndDate.getDate();
  const startMonth = MONTH_ABBREV[weekStartDate.getMonth()];
  const endMonth = MONTH_ABBREV[weekEndDate.getMonth()];
  const year = weekEndDate.getFullYear();

  if (weekStartDate.getMonth() === weekEndDate.getMonth()) {
    return `${startDay} – ${endDay} ${endMonth} ${year}`;
  }
  return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`;
}

/** Fecha única en español corto (ej. "28 sep 2026") — usada por el diálogo de "repetir patrón"
 * para mostrar hasta qué día llega la repetición, sin inventar un "número de semana" que la
 * app no maneja en ningún otro lado. */
export function formatDateEsShort(date: Date): string {
  return `${date.getDate()} ${MONTH_ABBREV[date.getMonth()]} ${date.getFullYear()}`;
}

export interface DayAssignment {
  /** Vacío en un descanso de rotación (no tiene turno, pero la persona sí cuenta ese día). */
  patternId: string;
  letter: ShiftLetter;
  workedHours: number;
  /** Origen del día. ROTATION = viene del patrón catorcenal asignado: no es un ajuste manual y no
   * se reenvía al guardar (hacerlo lo "congelaría" como ajuste individual). Sin valor = recién
   * asignado en pantalla (INDIVIDUAL). */
  source?: ScheduleSource;
}

export interface BulkPersonRow {
  id: string;
  fullName: string;
  documentNumber: string;
  initials: string;
  assignments: { [dayNumber: number]: DayAssignment | null };
  /** Vigencia del contrato — los días fuera de este rango deben salir deshabilitados. */
  contractStart: Date;
  contractEnd: Date | null;
  /** Fechas (dateKey) con reprogramación puntual, para el badge en la celda. */
  rescheduledDates: Set<string>;
  /** Días del rango sin turno vigente — si es > 0 conviene resaltar la fila. */
  unscheduledDays: number;
}

export function getInitials(fullName: string): string {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) { return ''; }
  if (parts.length === 1) { return parts[0].substring(0, 2).toUpperCase(); }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

/** Aplica un patrón a los días indicados de una persona. Devuelve una fila nueva (no muta la original). */
export function applyPatternToRowDays(row: BulkPersonRow, pattern: ShiftPattern, dayNumbers: number[]): BulkPersonRow {
  const assignments = { ...row.assignments };
  dayNumbers.forEach(dayNumber => {
    const patternDay = pattern.days[dayNumber];
    assignments[dayNumber] = {
      patternId: pattern.id,
      letter: patternDay.letter,
      workedHours: patternDay.workedHours
    };
  });
  return { ...row, assignments };
}

/** Quita la asignación de un solo día (deja la celda "Sin turno"). Devuelve una fila nueva. */
export function clearRowDay(row: BulkPersonRow, dayNumber: number): BulkPersonRow {
  const assignments = { ...row.assignments };
  assignments[dayNumber] = null;
  return { ...row, assignments };
}

export function computePersonWeekTotal(row: BulkPersonRow, maxWeeklyHours: number): { totalHours: number; exceeds: boolean } {
  const totalHours = Object.values(row.assignments)
    .filter((assignment): assignment is DayAssignment => !!assignment)
    .reduce((sum, assignment) => sum + assignment.workedHours, 0);
  const exceeds = maxWeeklyHours > 0 && totalHours > maxWeeklyHours;
  return { totalHours, exceeds };
}

export const ALL_DAY_NUMBERS = [0, 1, 2, 3, 4, 5, 6];

/**
 * Tipos y mapeo de la respuesta real de `api/Project/{projectId}/Schedule/Bulk` (ver
 * api-horarios-front.md sección 5). El GET trae en una sola llamada el personal real, el
 * catálogo de turnos que aparecen en el rango, y el paginado — ya no hace falta pedir
 * `api/WorkShift/Project/{id}` ni el personal por contrato aparte.
 */
export interface BulkWorkShiftDayApi {
  DayOfWeek: number;
  EntryTime: string;
  LeaveTime: string;
  WorkedHours: string;
  IsRest: boolean;
}

export interface BulkWorkShiftApi {
  Id: string;
  Name: string;
  IsDaily: boolean;
  Days: BulkWorkShiftDayApi[];
}

export interface BulkPersonApi {
  PersonalEmployerId: number;
  PersonalName: string;
  DocumentNumber: string;
  ContractStart: string;
  ContractEnd: string | null;
  Days: Array<string | null>;
  /** Alineado con `Days`: INDIVIDUAL | ROTATION | CONTRACT (nulo = sin programar). Un `Days[i]`
   * nulo con `Sources[i] = 'ROTATION'` es un descanso de rotación, no un día sin programar. */
  Sources?: Array<string | null>;
  RescheduledDates: string[];
  ScheduledDays: number;
  UnscheduledDays: number;
}

export interface BulkScheduleResponseApi {
  ProjectId: string;
  EmployerInfoId: string | null;
  From: string;
  To: string;
  EditableFrom: string | null;
  Page: number;
  PageSize: number;
  TotalPeople: number;
  TotalPages: number;
  Dates: string[];
  WorkShifts: BulkWorkShiftApi[];
  People: BulkPersonApi[];
}

export interface MappedBulkSchedule {
  patterns: ShiftPattern[];
  personnel: BulkPersonRow[];
  editableFrom: Date | null;
  page: number;
  pageSize: number;
  totalPeople: number;
  totalPages: number;
}

export interface BulkSaveItemApi {
  PersonalEmployerId: number;
  PersonalName: string;
  DocumentNumber: string;
  Status: 'APPLIED' | 'PARTIAL' | 'SKIPPED';
  AppliedFrom?: string;
  AppliedTo?: string;
  Reason?: string;
  Message?: string;
  SkippedDates?: string[];
  Warnings?: Array<{ Code?: string; Message?: string; Dates?: string[] }>;
}

export interface BulkSaveResultApi {
  Requested: number;
  Applied: number;
  Partial: number;
  Skipped: number;
  WithWarnings: number;
  Items: BulkSaveItemApi[];
}

/** Igual idea que buildShiftPatternFromWorkShift, pero el catálogo del bulk ya viene con las
 * horas resueltas por día de la semana (EntryTime/LeaveTime/WorkedHours/IsRest) — no hay que
 * derivar duraciones desde StartHour/EndHour crudos. */
export function buildShiftPatternFromBulkWorkShift(w: BulkWorkShiftApi): ShiftPattern {
  const baseLetter = getBaseLetterFromName(w.Name);
  const color = LETTER_COLORS[baseLetter];
  const byDayOfWeek = new Map<number, BulkWorkShiftDayApi>();
  (w.Days || []).forEach(d => byDayOfWeek.set(d.DayOfWeek, d));

  const workedDurations: number[] = [];
  byDayOfWeek.forEach(d => {
    if (!d.IsRest && !isZeroDurationRange(d.EntryTime, d.LeaveTime)) {
      workedDurations.push(extractMinutesFromApiTime(d.WorkedHours) / 60);
    }
  });
  const modalDuration = getModalDuration(workedDurations);

  const days: ShiftPatternDay[] = [];
  for (let dayNumber = 0; dayNumber < 7; dayNumber++) {
    const day = byDayOfWeek.get(dayNumber);
    if (!day || day.IsRest || isZeroDurationRange(day.EntryTime, day.LeaveTime)) {
      days.push({ dayNumber, hasSchedule: false, startHour: null, endHour: null, workedHours: 0, letter: 'D' });
      continue;
    }
    const workedHours = extractMinutesFromApiTime(day.WorkedHours) / 60;
    const letter: ShiftLetter = workedHours < modalDuration - 0.01 ? 'S' : baseLetter;
    days.push({ dayNumber, hasSchedule: true, startHour: day.EntryTime, endHour: day.LeaveTime, workedHours, letter });
  }

  return {
    id: w.Id,
    name: w.Name,
    isActive: true,
    color,
    days,
    totalWeekHours: days.reduce((sum, day) => sum + day.workedHours, 0),
    // Defensivo: si el backend llega a anidarlo como en WorkShiftConfiguration.Daily.IsDaily
    // en vez de un IsDaily plano, igual lo detectamos.
    isDaily: !!(w.IsDaily ?? (w as any).Daily?.IsDaily)
  };
}

/**
 * Mapea la respuesta del GET al modelo interno de la grilla masiva. `Dates[i]` y
 * `person.Days[i]` van alineados (mismo orden Lunes→Domingo que buildWeekDays, siempre que
 * from/to cubran exactamente una semana).
 */
export function mapBulkScheduleResponse(resp: BulkScheduleResponseApi): MappedBulkSchedule {
  const patterns = (resp.WorkShifts || []).map(buildShiftPatternFromBulkWorkShift);
  const patternById = new Map(patterns.map(p => [p.id, p]));
  const dates = resp.Dates || [];

  const personnel: BulkPersonRow[] = (resp.People || []).map(person => {
    const assignments: { [dayNumber: number]: DayAssignment | null } = {};
    dates.forEach((dateStr, i) => {
      const shiftId = person.Days[i];
      const source = (person.Sources?.[i] as ScheduleSource | null | undefined) || undefined;
      const dayNumber = new Date(dateStr).getDay();
      if (!shiftId) {
        // Descanso de rotación: sin turno, pero con programación (no cuenta como "sin turno").
        if (source === 'ROTATION') {
          assignments[dayNumber] = { patternId: '', letter: 'D', workedHours: 0, source };
        }
        return;
      }
      const patternDay = patternById.get(shiftId)?.days[dayNumber];
      assignments[dayNumber] = {
        patternId: shiftId,
        letter: patternDay?.letter || 'E',
        workedHours: patternDay?.workedHours || 0,
        source
      };
    });
    return {
      id: String(person.PersonalEmployerId),
      fullName: person.PersonalName || '',
      documentNumber: person.DocumentNumber || '',
      initials: getInitials(person.PersonalName || ''),
      assignments,
      contractStart: new Date(person.ContractStart),
      contractEnd: person.ContractEnd ? new Date(person.ContractEnd) : null,
      rescheduledDates: new Set((person.RescheduledDates || []).map(d => dateKey(new Date(d)))),
      unscheduledDays: person.UnscheduledDays || 0
    };
  });

  return {
    patterns,
    personnel,
    editableFrom: resp.EditableFrom ? new Date(resp.EditableFrom) : null,
    page: resp.Page,
    pageSize: resp.PageSize,
    totalPeople: resp.TotalPeople,
    totalPages: resp.TotalPages
  };
}

/**
 * Tarjeta de un patrón catorcenal (rotación) para la barra PATRONES de la masiva: cada día del
 * ciclo se resuelve contra los turnos del proyecto (letra y color), igual que un patrón semanal.
 * Solo informativa: según el PRD las rotaciones se asignan desde la programación de una persona,
 * no desde la masiva (que solo debe leerlas bien).
 */
export interface BulkRotationCard {
  id: string;
  name: string;
  cycleLengthDays: number;
  /** N días, indexados por día del ciclo (0…N−1). */
  days: Array<{ letter: ShiftLetter; color: string }>;
  totalHours: number;
}

export function buildBulkRotationCard(rotation: WorkShiftRotation, patterns: ShiftPattern[]): BulkRotationCard {
  let totalHours = 0;
  const days = rotation.days.map(day => {
    const turno = day.workShiftId ? patterns.find(p => p.id === day.workShiftId) : undefined;
    const workingDay = turno?.days.find(d => d.hasSchedule);
    if (day.isRest || !turno || !workingDay) {
      return { letter: 'D' as ShiftLetter, color: LETTER_COLORS.D };
    }
    totalHours += workingDay.workedHours;
    return { letter: workingDay.letter, color: workingDay.color || turno.color };
  });
  return { id: rotation.id, name: rotation.name, cycleLengthDays: rotation.cycleLengthDays, days, totalHours };
}
